// BlogPostForm.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../AuthContext";
import RichTextEditor from "../common/RichTextEditor";
import CategorySelector from "../common/CategorySelector";
import TagInput from "../common/TagInput";
import "./BlogPostForm.css";

const BlogPostForm = () => {
  const { postSlug } = useParams(); // Changed from postId to postSlug
  const isEditMode = !!postSlug; // Changed from postId to postSlug

  const [currentPostId, setCurrentPostId] = useState(null); // Added to store the actual _id for updates
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: "",
    category: "other",
    tags: [],
    featuredImage: "",
    isPublic: false,
    publicExcerpt: "",
  });
  const [featuredImageFile, setFeaturedImageFile] = useState(null);

  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { token, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isEditMode) {
      const fetchPost = async () => {
        try {
          setLoading(true);
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${postSlug}`, // Changed from postId to postSlug
            { headers: { Authorization: `Bearer ${token}` } }
          );

          setCurrentPostId(response.data._id); // Store the actual _id
          setFormData({
            title: response.data.title,
            content: response.data.content,
            excerpt: response.data.excerpt,
            category: response.data.category,
            tags: response.data.tags,
            featuredImage: response.data.featuredImage || "",
            isPublic: response.data.isPublic || false,
            publicExcerpt: response.data.publicExcerpt || "",
          });

          setError(null);
        } catch (err) {
          setError("Failed to fetch post details");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      fetchPost();
    }
  }, [postSlug, token, isEditMode]); // Changed from postId to postSlug

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    setFeaturedImageFile(e.target.files[0]);
    setFormData({
      ...formData,
      featuredImage: "", // Clear the existing image URL when a new file is selected
    });
  };

  const handleContentChange = (content) => {
    setFormData({
      ...formData,
      content,
    });
  };

  const handleCategoryChange = (category) => {
    setFormData({
      ...formData,
      category,
    });
  };

  const handleTagsChange = (tags) => {
    setFormData({
      ...formData,
      tags,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }

    if (!formData.content.trim()) {
      setError("Content is required");
      return;
    }

    if (!formData.excerpt.trim()) {
      setError("Excerpt is required");
      return;
    }

    if (formData.isPublic && !formData.publicExcerpt.trim()) {
      setError("Public excerpt is required when post is public");
      return;
    }

    // Generate slug from title
    const slug = formData.title
      .toLowerCase()
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, "-");

    try {
      setSaving(true);

      const submissionData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "tags") {
          submissionData.append(key, JSON.stringify(formData[key]));
        } else {
          submissionData.append(key, formData[key]);
        }
      });
      submissionData.append("slug", slug);
      submissionData.append("author", user.id);
      if (featuredImageFile) {
        submissionData.append("featuredImage", featuredImageFile);
      }

      if (isEditMode) {
        // First, update the post content
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${currentPostId}`,
          submissionData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        // Then, submit it for approval
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${currentPostId}/submit`,
          {}, // No body needed for submit endpoint
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // For new posts, the default status is 'draft', this button submits it directly
        const newPostResponse = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/blog/posts`,
          submissionData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        // If creating a new post and submitting directly, call the submit endpoint
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${newPostResponse.data._id}/submit`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Redirect based on user role
      if (user.role === "blogger") {
        navigate("/blogger");
      } else {
        navigate("/staff/blog");
      }
    } catch (err) {
      setError(
        `Failed to ${isEditMode ? "update" : "create"} post: ${
          err.response?.data?.message || err.message
        }`
      );
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    // Validate minimum required fields
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }

    // Generate slug from title
    const slug = formData.title
      .toLowerCase()
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, "-");

    try {
      setSaving(true);

      const submissionData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "tags") {
          submissionData.append(key, JSON.stringify(formData[key]));
        } else {
          submissionData.append(key, formData[key]);
        }
      });
      submissionData.append("slug", slug);
      submissionData.append("author", user.id);
      if (featuredImageFile) {
        submissionData.append("featuredImage", featuredImageFile);
      }

      if (isEditMode) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${currentPostId}`, // Changed from postId to currentPostId
          submissionData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/blog/posts`,
          submissionData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      // Redirect based on user role
      if (user.role === "blogger") {
        navigate("/blogger");
      } else {
        navigate("/staff/blog");
      }
    } catch (err) {
      setError(
        `Failed to save draft: ${err.response?.data?.message || err.message}`
      );
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading post data...</div>;

  return (
    <div className="blog-post-form">
      <h1>{isEditMode ? "Edit Blog Post" : "Create New Blog Post"}</h1>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="excerpt">Excerpt (Summary) *</label>
          <textarea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleInputChange}
            rows={3}
            required
          />
          <small>A brief summary of the post (shown in listings)</small>
        </div>

        <div className="form-group">
          <label>Content *</label>
          <RichTextEditor
            initialContent={formData.content}
            onChange={handleContentChange}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Category</label>
            <CategorySelector
              selected={formData.category}
              onChange={handleCategoryChange}
            />
          </div>

          <div className="form-group">
            <label>Tags</label>
            <TagInput tags={formData.tags} onChange={handleTagsChange} />
            <small>Press Enter or comma to add a tag</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="featuredImage">Featured Image</label>
          <input
            type="file"
            id="featuredImage"
            name="featuredImage"
            onChange={handleFileChange}
          />
          {featuredImageFile && (
            <div className="image-preview">
              <img
                src={URL.createObjectURL(featuredImageFile)}
                alt="Featured Preview"
              />
            </div>
          )}
          {!featuredImageFile && formData.featuredImage && (
            <div className="image-preview">
              <img
                src={`${process.env.REACT_APP_API_BASE_URL}${formData.featuredImage}`}
                alt="Featured"
              />
            </div>
          )}
        </div>

        <div className="form-section">
          <h3>Public Preview Settings</h3>
          <p className="section-description">
            Control how your post appears to non-members in the blog preview
            section.
          </p>

          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="isPublic"
              name="isPublic"
              checked={formData.isPublic}
              onChange={handleInputChange}
            />
            <label htmlFor="isPublic">
              <strong>Show preview to non-members</strong> (make this post
              visible in public blog preview)
            </label>
          </div>

          {formData.isPublic && (
            <div className="form-group">
              <label htmlFor="publicExcerpt">Public Preview Excerpt *</label>
              <textarea
                id="publicExcerpt"
                name="publicExcerpt"
                value={formData.publicExcerpt}
                onChange={handleInputChange}
                rows={3}
                required={formData.isPublic}
              />
              <small>
                This excerpt will be shown to non-members in the public blog
                preview. Make it engaging to encourage readers to sign up for
                full access.
              </small>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => {
              if (user.role === "blogger") {
                navigate("/blogger");
              } else {
                navigate("/staff/blog");
              }
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            className="draft-btn"
            onClick={handleSaveDraft}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save as Draft"}
          </button>

          <button type="submit" className="submit-btn" disabled={saving}>
            {saving ? "Submitting..." : "Submit for Approval"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogPostForm;
